import { getPage, getPageType, getGlobal, getAllProductPages } from 'lib/contentful';
import { fetchAllLocales } from 'lib/contentful/api';
import { useSalons } from 'hooks';
import SEO from 'components/shared/SEO';
import PageProduct from 'components/pages/product';
import Layout from 'components/layout';
import {
  reformatContentfulLocaleToLocale,
  reformatLocaleToContentfulLocale,
} from 'lib/utils/helpers';
import PageProductBundle from 'components/pages/productBundle/productBundle';
import CountrySelectorModal from 'components/pages/countrySelector';
import getRatings from 'lib/reviews/getRatings';
import groupRating from 'lib/reviews/groupRating';
import { NEW_SCHEMA_LOCALES } from 'lib/utils/getNewSchema';

export default function Page({ page, locale, type, previewData, globalModules }) {
  useSalons(locale);

  // // If the page is not yet generated, this will be displayed
  // // initially until getStaticProps() finishes running
  // if (router.isFallback) {
  //   return <div>Loading...</div>;
  // }

  const renderPageContent = () => {
    switch (type) {
      case 'PageProduct':
        return <PageProduct locale={locale} data={page} />;
      case 'PageProductBundle':
        return <PageProductBundle locale={locale} data={page} />;
      default:
        return <div>{`Cannot find page type ${type}`}</div>;
    }
  };

  return (
    <Layout locale={locale} pageData={page} previewData={previewData} globalModules={globalModules}>
      <>
        {page?.seo && <SEO seo={page.seo} pageData={page} product />}
        {renderPageContent()}
        <CountrySelectorModal pageData={page} locale={locale} />
      </>
    </Layout>
  );
}

export async function getStaticProps({ locale, params, preview = false, previewData = null }) {
  const { p } = params;
  const reformattedLocale = reformatLocaleToContentfulLocale(locale);
  const type = await getPageType(p, reformattedLocale);
  const page = await getPage(type, p, reformattedLocale, preview, null);

  if (!page) {
    return {
      notFound: true,
    };
  }

  const realLocale = reformatContentfulLocaleToLocale(reformattedLocale);
  if (NEW_SCHEMA_LOCALES.includes(realLocale) && page?.shopifyData?.variants?.[0]?.sku) {
    try {
      const { sku } = page.shopifyData.variants[0];
      const group = groupRating(
        await getRatings({ skuArr: [sku], locale: reformattedLocale }),
        sku
      );
      if (group) {
        page.ratingData = { average_rating: group.average, num_ratings: group.total };
      }
    } catch (e) {
      // Ratings are non-critical, continue without them
    }
  }

  const globalModules = await getGlobal(reformattedLocale);

  return {
    props: { page, locale, type, previewData, globalModules },
  };
}

export async function getStaticPaths() {
  const contentfulLocales = await fetchAllLocales();

  /*
  * contentfulLocales [
  'en',    'nl-NL', 'nl-BE',
  'hr',    'de-DE', 'fr',
  'sr',    'sl',    'en-US',
  'fr-BE', 'fr-FR', 'bs',
  'en-GB', 'es-ES', 'ro'
] */

  const allPages = [];
  const pagesPromises = [];

  const saveLocale = async locale => {
    const productPagesAndBundles = await getAllProductPages(locale);

    allPages.push(...productPagesAndBundles);
  };

  contentfulLocales.forEach(locale => {
    pagesPromises.push(saveLocale(reformatContentfulLocaleToLocale(locale)));
  });

  await Promise.all(pagesPromises);

  const finalPaths = [];

  allPages.forEach(page => {
    finalPaths.push({
      params: {
        p: page.slug,
      },
      locale: page.locale,
    });
  });

  return {
    paths: finalPaths,
    fallback: 'blocking',
  };
}
